import {
  formatCategoryForDisplay,
  type ChatMentionsMenuItemType,
} from '@terra/ui-design-system';
import type {
  ObjectContextKind,
  ObjectContextMarkedField,
  ObjectContextMentionData,
  ObjectContextSelection,
} from '@terra/shared-dto-chat-types';
import type { InternalEnrichedFinding } from '@terra/ui-design-system';
import type { SignalDto } from '@terra/shared-contracts-signals';
import {
  getFindingPocText,
  parseEditablePocBlocks,
} from './findingPocContextText';
import {
  BookOpen01Icon,
  Target01Icon,
  ViewIcon,
  FileCodeIcon,
  Globe02Icon,
  Route01Icon,
  Target03Icon,
  TextSelectIcon,
  FlashIcon,
  Message01Icon,
} from '@terra/ui-icons';
import { createElement } from 'react';

const OBJECT_CONTEXT_KIND_ICONS: Record<ObjectContextKind, typeof ViewIcon> = {
  Finding: ViewIcon,
  Signal: Target03Icon,
  EndPoint: FlashIcon,
  WebPage: Globe02Icon,
  Trace: Route01Icon,
  Skill: BookOpen01Icon,
  SourceCode: FileCodeIcon,
  Chat: Message01Icon,
};

type CreateObjectContextMentionParams = {
  objectKind: ObjectContextKind;
  objectId: string;
  objectLabel: string;
  fields?: ObjectContextMarkedField[];
  selection?: ObjectContextSelection;
  instruction?: string;
};

function formatObjectContextRawText({
  objectKind,
  objectId,
  objectLabel,
  fields,
  selection,
  instruction,
}: CreateObjectContextMentionParams): string {
  const lines: string[] = [`${objectKind} "${objectLabel}" (ID: ${objectId})`];

  if (!selection && fields && fields.length > 0) {
    for (const field of fields) {
      lines.push(`${field.label} (${field.field}):`);
      lines.push(field.value);
    }
  }

  if (selection) {
    lines.push(selection.excerpt);
  }

  if (instruction) {
    lines.push(instruction);
  }

  return lines.join('\n');
}

function truncateObjectContextLabel(label: string, maxLength: number): string {
  return label.length > maxLength
    ? `${label.slice(0, maxLength - 3)}...`
    : label;
}

function buildMentionLabel({
  objectKind,
  objectLabel,
  fields,
  selection,
}: Pick<
  CreateObjectContextMentionParams,
  'objectKind' | 'objectLabel' | 'fields' | 'selection'
>): string {
  if (selection?.field) {
    return `${objectKind} · ${selection.field}`;
  }

  if (fields && fields.length === 1) {
    return `${objectKind} · ${fields[0].label}`;
  }

  return `${objectKind} · ${truncateObjectContextLabel(objectLabel, 32)}`;
}

export function buildObjectContextKey({
  objectKind,
  objectId,
}: {
  objectKind: string;
  objectId: string;
}): string {
  return `${objectKind}:${objectId}`;
}

function buildSelectionKey({ field, excerpt }: ObjectContextSelection): string {
  const excerptHash = Array.from(excerpt).reduce(
    (accumulator, character) =>
      (Math.imul(accumulator, 31) + character.charCodeAt(0)) | 0,
    0,
  );

  return `${field ?? 'selection'}-${(excerptHash >>> 0).toString(36)}`;
}

export function createObjectContextMention({
  objectKind,
  objectId,
  objectLabel,
  fields,
  selection,
  instruction,
}: CreateObjectContextMentionParams): ChatMentionsMenuItemType {
  const rawText = formatObjectContextRawText({
    objectKind,
    objectId,
    objectLabel,
    fields,
    selection,
    instruction,
  });
  const mentionLabel = buildMentionLabel({
    objectKind,
    objectLabel,
    fields,
    selection,
  });
  const objectContext: ObjectContextMentionData = {
    objectKind,
    objectId,
    objectLabel,
    fields,
    selection,
    rawText,
  };
  const hasSelection = Boolean(selection?.excerpt);
  const mentionKey = selection
    ? `${objectId}:${buildSelectionKey(selection)}`
    : objectId;

  return {
    id: `object-context-${objectKind}-${mentionKey}`,
    label: mentionLabel,
    icon: createElement(
      hasSelection
        ? TextSelectIcon
        : (OBJECT_CONTEXT_KIND_ICONS[objectKind] ?? Target01Icon),
      { size: 12 },
    ),
    type: 'resource',
    mentionEntity: {
      entityType: 'ObjectContext',
      entityId: `${objectKind}:${mentionKey}`,
      label: mentionLabel,
      extraData: {
        objectContext,
      },
    },
  };
}

export function computeLineRangeFromText({
  fullText,
  selectedText,
}: {
  fullText: string;
  selectedText: string;
}):
  | {
      lineStart: number;
      lineEnd: number;
    }
  | undefined {
  const trimmedSelection = selectedText.trim();

  if (!trimmedSelection) {
    return undefined;
  }

  const selectionIndex = fullText.indexOf(trimmedSelection);

  if (selectionIndex < 0) {
    return undefined;
  }

  const beforeSelection = fullText.slice(0, selectionIndex);
  const lineStart = beforeSelection.split('\n').length;
  const lineEnd = lineStart + trimmedSelection.split('\n').length - 1;

  return { lineStart, lineEnd };
}

function findingEndpointId(source: {
  assets?: ReadonlyArray<{ type: string; id: string }> | null;
}): string | undefined {
  return source.assets?.find((asset) => asset.type === 'ENDPOINT')?.id;
}

function buildFindingContextFields(
  finding: InternalEnrichedFinding,
): ObjectContextMarkedField[] {
  const fields: ObjectContextMarkedField[] = [
    { field: 'title', label: 'Title', value: finding.title },
    { field: 'severity', label: 'Severity', value: finding.severity },
    { field: 'status', label: 'Status', value: finding.status },
  ];

  if (finding.shortDescription) {
    fields.push({
      field: 'shortDescription',
      label: 'Short Description',
      value: finding.shortDescription,
    });
  }

  if (finding.description) {
    fields.push({
      field: 'description',
      label: 'Description',
      value: finding.description,
    });
  }

  const pocText = getFindingPocText({
    poc: finding.poc,
    pocBlocks: finding.pocBlocks,
  });

  if (pocText) {
    fields.push({
      field: 'poc',
      label: 'Proof of Concept',
      value: pocText,
    });
  }

  if (finding.businessImpact) {
    fields.push({
      field: 'businessImpact',
      label: 'Business Impact',
      value: finding.businessImpact,
    });
  }

  if (finding.mitigationPlan) {
    fields.push({
      field: 'mitigationPlan',
      label: 'Mitigation Plan',
      value: finding.mitigationPlan,
    });
  }

  const endpointId = findingEndpointId(finding);

  if (endpointId) {
    fields.push({
      field: 'endpointId',
      label: 'Endpoint ID',
      value: endpointId,
    });
  }

  return fields;
}

export function buildFindingContextFieldsFromEditable({
  editable,
  fallbackTitle,
}: {
  editable: {
    title: string;
    shortDescription?: string | null;
    description?: string | null;
    poc?: string | null;
    pocBlocks?: unknown;
    businessImpact?: string | null;
    mitigationPlan?: string | null;
    severity?: string;
    status?: string;
    assets?: ReadonlyArray<{ type: string; id: string }> | null;
  };
  fallbackTitle?: string;
}): ObjectContextMarkedField[] {
  const fields: ObjectContextMarkedField[] = [
    {
      field: 'title',
      label: 'Title',
      value: editable.title || fallbackTitle || '',
    },
    { field: 'severity', label: 'Severity', value: editable.severity ?? '' },
    { field: 'status', label: 'Status', value: editable.status ?? '' },
  ];

  if (editable.shortDescription) {
    fields.push({
      field: 'shortDescription',
      label: 'Short Description',
      value: editable.shortDescription,
    });
  }

  if (editable.description) {
    fields.push({
      field: 'description',
      label: 'Description',
      value: editable.description,
    });
  }

  const editablePocBlocks = parseEditablePocBlocks(editable.pocBlocks);
  const pocText = getFindingPocText({
    poc: editable.poc,
    pocBlocks: editablePocBlocks,
  });

  if (pocText) {
    fields.push({
      field: 'poc',
      label: 'Proof of Concept',
      value: pocText,
    });
  }

  if (editable.businessImpact) {
    fields.push({
      field: 'businessImpact',
      label: 'Business Impact',
      value: editable.businessImpact,
    });
  }

  if (editable.mitigationPlan) {
    fields.push({
      field: 'mitigationPlan',
      label: 'Mitigation Plan',
      value: editable.mitigationPlan,
    });
  }

  const endpointId = findingEndpointId(editable);

  if (endpointId) {
    fields.push({
      field: 'endpointId',
      label: 'Endpoint ID',
      value: endpointId,
    });
  }

  return fields.filter((field) => field.value.trim().length > 0);
}

export { buildFindingContextFields };
export function createFindingObjectContextMention(
  finding: InternalEnrichedFinding,
  {
    fields,
    selection,
    instruction,
  }: {
    fields?: ObjectContextMarkedField[];
    selection?: ObjectContextSelection;
    instruction?: string;
  } = {},
): ChatMentionsMenuItemType {
  const endpointId = findingEndpointId(finding);
  const endpointInstruction = endpointId
    ? `Endpoint ID: ${endpointId}`
    : undefined;

  return createObjectContextMention({
    objectKind: 'Finding',
    objectId: finding.id,
    objectLabel: finding.title,
    fields:
      selection !== undefined
        ? undefined
        : (fields ?? buildFindingContextFields(finding)),
    selection,
    instruction:
      selection !== undefined
        ? [endpointInstruction, instruction]
            .filter((value): value is string => Boolean(value))
            .join('\n') || undefined
        : instruction,
  });
}

export function getSignalObjectLabel(signal: SignalDto): string {
  const description = signal.description?.trim();

  if (description) {
    return description;
  }

  return `Signal ${signal.id.slice(-4)}`;
}

export function buildSignalContextFields(
  signal: SignalDto,
): ObjectContextMarkedField[] {
  const fields: ObjectContextMarkedField[] = [];

  if (signal.description) {
    fields.push({
      field: 'description',
      label: 'Description',
      value: signal.description,
    });
  }

  if (signal.analysis) {
    fields.push({
      field: 'analysis',
      label: 'Analysis',
      value: signal.analysis,
    });
  }

  if (signal.category) {
    fields.push({
      field: 'category',
      label: 'Category',
      value: formatCategoryForDisplay(signal.category),
    });
  }

  if (signal.rank) {
    fields.push({
      field: 'rank',
      label: 'Rank',
      value: signal.rank,
    });
  }

  if (signal.reason) {
    fields.push({
      field: 'reason',
      label: 'Reason',
      value: signal.reason,
    });
  }

  if (signal.securityTestCaseSummary) {
    fields.push({
      field: 'securityTestCaseSummary',
      label: 'Security Test Case Summary',
      value: signal.securityTestCaseSummary,
    });
  }

  if (signal.smokeTestSummary) {
    fields.push({
      field: 'smokeTestSummary',
      label: 'Smoke Test Summary',
      value: signal.smokeTestSummary,
    });
  }

  return fields;
}

export function createSignalObjectContextMention(
  signal: SignalDto,
  {
    fields,
    selection,
    instruction,
  }: {
    fields?: ObjectContextMarkedField[];
    selection?: ObjectContextSelection;
    instruction?: string;
  } = {},
): ChatMentionsMenuItemType {
  return createObjectContextMention({
    objectKind: 'Signal',
    objectId: signal.id,
    objectLabel: getSignalObjectLabel(signal),
    fields:
      selection !== undefined
        ? undefined
        : (fields ?? buildSignalContextFields(signal)),
    selection,
    instruction,
  });
}
